import { z } from 'zod'

/**
 * Painel do admin: o que está parado e com quem.
 *
 * A pendência não é gravada em lugar nenhum — é calculada na hora, a partir do
 * que o sistema já registra. Gravar criaria uma segunda verdade para manter em
 * dia com a primeira, e o dia em que as duas divergissem seria o dia em que o
 * painel passaria a mentir.
 *
 * O que fica guardado é a **baixa**: a decisão de que aquilo já foi resolvido,
 * com autor, hora e motivo.
 */

export const GRUPOS_PENDENCIA = [
  'cliente_esperando',
  'compromisso',
  'aviso_sem_acao',
  'cadastro',
] as const
export type GrupoPendencia = (typeof GRUPOS_PENDENCIA)[number]

export const ROTULO_GRUPO_PENDENCIA: Record<GrupoPendencia, string> = {
  cliente_esperando: 'Cliente esperando',
  compromisso: 'Compromisso não cumprido',
  aviso_sem_acao: 'Aviso sem ação',
  cadastro: 'Cadastro e saúde',
}

export const TIPOS_PENDENCIA = [
  'conversa_sem_resposta',
  'chamado_na_fila',
  'tarefa_atrasada',
  'agendamento_falhou',
  'agendamento_vencido',
  'retorno_parado',
  'negociacao_parada',
  'cliente_sem_dono',
  'contato_sem_numero',
  'conexao_caida',
] as const
export type TipoPendencia = (typeof TIPOS_PENDENCIA)[number]

export const GRUPO_DO_TIPO: Record<TipoPendencia, GrupoPendencia> = {
  conversa_sem_resposta: 'cliente_esperando',
  chamado_na_fila: 'cliente_esperando',
  tarefa_atrasada: 'compromisso',
  agendamento_falhou: 'compromisso',
  agendamento_vencido: 'compromisso',
  retorno_parado: 'compromisso',
  negociacao_parada: 'aviso_sem_acao',
  cliente_sem_dono: 'cadastro',
  contato_sem_numero: 'cadastro',
  conexao_caida: 'cadastro',
}

export const ROTULO_TIPO_PENDENCIA: Record<TipoPendencia, string> = {
  conversa_sem_resposta: 'Conversa sem resposta',
  chamado_na_fila: 'Parado na fila',
  tarefa_atrasada: 'Tarefa atrasada',
  agendamento_falhou: 'Mensagem programada falhou',
  agendamento_vencido: 'Mensagem programada não saiu',
  retorno_parado: 'Retorno marcado e não feito',
  negociacao_parada: 'Negociação parada na etapa',
  cliente_sem_dono: 'Cliente sem vendedor',
  contato_sem_numero: 'Contato sem número',
  conexao_caida: 'Número desconectado',
}

export interface PendenciaDto {
  tipo: TipoPendencia
  grupo: GrupoPendencia
  /** O que está pendente: chamado, contato, tarefa, negociação… */
  entidadeId: string
  titulo: string
  detalhe: string
  /** De quem é a pendência. `null` é o pior caso: ninguém a quem perguntar. */
  donoId: string | null
  donoNome: string | null
  /** Desde quando está assim. */
  desde: string
  dias: number
  /** Para abrir a conversa no ponto, quando existe uma. */
  chamadoId: string | null
  contatoId: string | null
  /**
   * O sistema já cobrou isto antes e continua igual.
   *
   * É a diferença entre "ninguém viu" e "viram e não fizeram" — e é a segunda
   * que interessa a quem abre este painel.
   */
  jaCobrado: boolean
}

export interface PessoaComPendencias {
  usuarioId: string | null
  nome: string
  total: number
  porGrupo: Record<GrupoPendencia, number>
  /** A pendência mais antiga da pessoa, em dias. */
  maisAntigaEmDias: number
}

export interface PainelDto {
  pendencias: PendenciaDto[]
  pessoas: PessoaComPendencias[]
  porGrupo: Record<GrupoPendencia, number>
  total: number
  /** Quando a conta foi feita: a tela mostra, porque o número é do instante. */
  geradoEm: string
  /** Os cortes em vigor, para a tela explicar de onde sai cada lista. */
  cortes: {
    semRespostaHoras: number
    filaHoras: number
    negociacaoDias: number
  }
}

export interface BaixaDto {
  id: string
  tipo: TipoPendencia | string
  entidadeId: string
  resumo: string
  motivo: string
  donoNome: string | null
  autorNome: string | null
  criadoEm: string
}

export const darBaixaSchema = z.object({
  tipo: z.enum(TIPOS_PENDENCIA),
  entidadeId: z.string().uuid(),
  resumo: z.string().trim().min(1).max(200),
  donoId: z.string().uuid().nullable().optional(),
  /*
    Motivo obrigatório, com tamanho mínimo de frase.

    O campo existe para o painel continuar servindo depois de virar rotina: uma
    baixa sem explicação é indistinguível de um clique para limpar a tela, e é
    exatamente o que se quer poder conferir mais tarde.
  */
  motivo: z.string().trim().min(3, 'Escreva o motivo da baixa.').max(300),
})
export type DarBaixaEntrada = z.infer<typeof darBaixaSchema>
